import React, { useState, useEffect, useContext } from "react";
import { MasajidContext } from "../contexts/MasajidContext";
import { useParams, useNavigate } from "react-router-dom";
import MasjidInfo from "./MasjidInfo";
import Header from "./Header";
import BottomNavBar from "./BottomNavBar";

// Cache object to store API responses
const apiCache = {};
const CACHE_DURATION = 60000; // 1 minute

function MasjidDetails() {
  const { language } = useContext(MasajidContext);
  const { username } = useParams();
  const navigate = useNavigate();
  const [masjidInfo, setMasjidInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  useEffect(() => {
    const fetchMasjidInfo = async () => {
      const now = Date.now();
      const cached = apiCache[username];

      if (cached && now - cached.timestamp < CACHE_DURATION) {
        setMasjidInfo(cached.data);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const headers = {
          Authorization: username,
          "Content-Type": "application/json",
        };

        const detailsResponse = await fetch(`/api/v1/masjid/details`, {
          headers,
        });

        if (!detailsResponse.ok) {
          navigate("/not-found", { replace: true });
          return;
        }

        const masjidDetails = await detailsResponse.json();
        const isTimingsFixed = masjidDetails?.timingsFixed;

        const [
          timingsTodayResponse,
          timingsExtraResponse,
          announcementsResponse,
        ] = await Promise.all([
          fetch(`/api/v1/timings/today`, { headers }),
          fetch(
            isTimingsFixed
              ? `/api/v1/timings/fixed-times`
              : `/api/v1/timings/next-changes`,
            { headers },
          ),
          fetch(`/api/v1/announcements/current`, { headers }),
        ]);

        const timingsToday = await timingsTodayResponse.json();
        const timingsExtra = await timingsExtraResponse.json();
        const announcements = await announcementsResponse.json();

        const data = {
          details: masjidDetails,
          timings: timingsToday,
          ...(isTimingsFixed
            ? { fixedTimes: timingsExtra }
            : { nextChanges: timingsExtra }),
          announcements: announcements.currentAnnouncements,
        };

        apiCache[username] = {
          data: data,
          timestamp: Date.now(),
        };

        setMasjidInfo(data);
      } catch (error) {
        console.error("Failed to fetch detailed data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchMasjidInfo();
  }, [username, navigate]);

  return (
    <>
      <Header />

      <section className="section">
        {!isOnline && (
          <div style={{ marginTop: 64, textAlign: "center" }}>
            <h1
              style={{ color: "#f63c3c", marginTop: 64 }}
              lang={language === "urdu" && "ur"}
            >
              {language === "urdu"
                ? "آپ کا انٹرنیٹ کام نہیں کر رہا"
                : "Please connect to the internet"}
            </h1>
          </div>
        )}

        {loading && (
          <div
            className="d-flex justify-content-center"
            style={{ marginTop: 24 }}
          >
            <div
              className="spinner-border"
              role="status"
              style={{ color: "rgb(255, 255, 255, 0.8)" }}
            >
              <span className="sr-only">Loading...</span>
            </div>
          </div>
        )}

        {masjidInfo && !loading && (
          <MasjidInfo
            timings={masjidInfo.timings}
            nextChanges={masjidInfo.nextChanges}
            fixedTimes={masjidInfo.fixedTimes}
            announcements={masjidInfo.announcements}
            details={masjidInfo.details}
            username={username}
          />
        )}
      </section>
      <BottomNavBar />
    </>
  );
}

export default MasjidDetails;
